<!-- /frontend/src/components/events/CancelEventDialog.vue -->
<template>
  <div
    v-if="open"
    class="dialog-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="dialog-title"
    @keydown.esc.prevent="onClose"
  >
    <div
      ref="dialogRef"
      class="dialog-card"
      tabindex="-1"
    >
      <header class="page-header">
        <h2
          id="dialog-title"
          class="h1"
        >
          Ausfall des Angebotes
        </h2>
      </header>

      <form
        class="form"
        @submit.prevent="onSubmit"
      >
        <!-- Grund -->
        <div class="field">
          <label for="reason">Grund</label>
          <select
            id="reason"
            v-model="form.reason"
            required
            class="input"
          >
            <option
              disabled
              value=""
            >
              Bitte wählen
            </option>

            <!-- häufige & sinnvolle Gründe -->
            <option value="Krankheit">
              Krankheit
            </option>
            <option value="Fortbildung">
              Fortbildung
            </option>
            <option value="Dienstreise">
              Dienstreise
            </option>
            <option value="Personalmangel">
              Personalmangel
            </option>
            <option value="Raum gesperrt">
              Raum gesperrt
            </option>
            <option value="Prüfung / Klausur">
              Prüfung / Klausur
            </option>
            <option value="Exkursion / Ausflug">
              Exkursion / Ausflug
            </option>
            <option value="Schulausfall (Witterung)">
              Schulausfall (Witterung)
            </option>
            <option value="Technische Störung">
              Technische Störung
            </option>
            <option value="Sicherheitsgründe">
              Sicherheitsgründe
            </option>
            <option value="Organisatorische Gründe">
              Organisatorische Gründe
            </option>
            <option value="Behördliche Anordnung">
              Behördliche Anordnung
            </option>

            <!-- Restkategorie -->
            <option value="Sonstiges">
              Sonstiges
            </option>
          </select>
          <p class="help">
            Wähle den zutreffendsten Grund aus.
          </p>
        </div>

        <!-- Freitext nur bei "Sonstiges" -->
        <div
          v-if="form.reason === 'Sonstiges'"
          class="field"
        >
          <label for="reasonText">Kurzbeschreibung (optional)</label>
          <input
            id="reasonText"
            v-model.trim="form.reasonText"
            type="text"
            class="input"
            maxlength="255"
            placeholder="z. B. kurzfristige interne Abstimmung"
          >
          <p class="help">
            Optionaler Zusatz, max. 255 Zeichen.
          </p>
        </div>

        <!-- Fehlermeldung -->
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>

        <!-- Aktionen -->
        <div class="actions">
          <button
            type="button"
            class="btn"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner"
              aria-hidden="true"
            />
            Bestätigen
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { eventsApi } from '@/api/events'

const props = defineProps({
  open: { type: Boolean, default: false },
  eventId: { type: [Number, String], required: true }
})
const emit = defineEmits(['close', 'canceled'])

const dialogRef = ref(null)
const loading = ref(false)
const error = ref(null)

const form = ref({
  reason: '',
  reasonText: ''
})

function reset() {
  error.value = null
  loading.value = false
  form.value = {
    reason: '',
    reasonText: ''
  }
}

function onClose() {
  emit('close')
}

async function onSubmit() {
  error.value = null
  loading.value = true
  try {
    if (!form.value.reason) {
      error.value = 'Bitte einen Grund auswählen.'
      return
    }

    // API-Vertrag: ein String-Feld "reason"
    const reason =
        form.value.reason === 'Sonstiges' && form.value.reasonText
            ? `${form.value.reason} — ${form.value.reasonText}`
            : form.value.reason

    const payload = { reason }

    const res = await eventsApi.cancelEvent(props.eventId, payload)
    const canceled = res?.data?.canceled ?? res?.data ?? true

    emit('canceled', canceled)
    emit('close')

    // Erfolg-Toast auslösen (globaler Event, von deiner Toast-Komponente konsumiert)
    window.dispatchEvent(
        new CustomEvent('app:toast', {
          detail: { type: 'success', message: 'Angebot als ausgefallen markiert.' }
        })
    )
  } catch (e) {
    // Grobe Fehlerbehandlung
    error.value =
        e?.response?.data?.error?.message ||
        e?.message ||
        'Aktion fehlgeschlagen. Bitte erneut versuchen.'
  } finally {
    loading.value = false
  }
}

watch(
    () => props.open,
    (o) => {
      if (o) {
        // Fokus ins Dialogfenster legen
        requestAnimationFrame(() => dialogRef.value?.focus?.())
      } else {
        reset()
      }
    }
)

onMounted(() => {
  // NOP
})
</script>

<style scoped>
:root {
  --surface: #ffffff;
  --overlay: rgba(0, 0, 0, 0.35);
  --border: #e2e8f0;
  --text-muted: #64748b;
  --primary: #2563eb;
  --primary-600: #1d4ed8;
}

.dialog-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  z-index: 1000;
}

.dialog-card {
  width: min(560px, 100%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.2);
  padding: 14px 14px 12px;
  outline: none;
}

.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.h1 {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  margin: 0;
}

.form {
  display: grid;
  gap: 12px;
}

.field {
  display: grid;
  gap: 6px;
}

.input,
select {
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
}

input#reasonText {
  height: 38px;
}

.help {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.btn {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
  animation: spin 1s linear infinite;
  vertical-align: -3px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
}

.notice-danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: #fca5a5;
  color: #991b1b;
}

.mt-12 {
  margin-top: 12px;
}
</style>
